import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import useCustomRouter from '@/hooks/useCustomRouter';
import useAccount from '@/lib/hooks/useAccount';
import useCart from '@/lib/hooks/useCart';
import useStores from '@/lib/hooks/useStores';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { useDebounce } from '@/hooks/useDebounce';
import useProductSearch from '@/lib/hooks/useProductSearch';
import toast from 'react-hot-toast';
import { mapBusinessUnit } from '@/utils/mappers/map-business-unit';
import { mapStore } from '@/utils/mappers/map-store';
import { BusinessUnit } from '@shared/types/business-unit';
import { Store } from '@shared/types/store';
import useSwrClearCache from '@/hooks/useSwrClearCache';
import { mapProductSuggestion } from '@/utils/mappers/map-product-suggestion';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { useCustomObjects } from '@/lib/hooks/useCustomObjects';
import { ChannelVisibility } from '@shared/types/custom-object/ChannelsVisibilitySettings';

const useHeaderData = () => {
  const router = useCustomRouter();
  const clearCache = useSwrClearCache();
  const { translate } = useTranslation();
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query');
  const { stores, channel, priceChannel } = useStores();
  const { account, logout, updateSessionData, businessUnitKey: sessionBusinessUnitKey } = useAccount();
  const {
    businessUnits,
    defaultBusinessUnit,
    selectedBusinessUnit,
    selectedStore,
    setSelectedBusinessUnit,
    setSelectedStore,
  } = useStoreAndBusinessUnits();

  const channelsVisibilitySettings: ChannelVisibility[] =
    useCustomObjects<ChannelVisibility[]>('channelsVisibilitySettings', 'channels-visibility-list') ?? [];
  const channelsVisibilityKeys = channelsVisibilitySettings.map((item) => item.key);

  const onBusinessUnitSelect = useCallback(
    async (businessUnitKey: string, reload = false) => {
      const bu = businessUnits?.find((bu) => bu.key === businessUnitKey) as BusinessUnit;

      if (!bu) return;

      const st = bu?.stores?.find((st) => st.key === bu.key) as Store;

      if (!st) return;
      if (st.key === selectedStore?.key) return;
      setSelectedBusinessUnit(mapBusinessUnit(bu));
      setSelectedStore(mapStore(st));

      const distributionChannel = st.distributionChannels?.find((channel) =>
        channel.key ? !channelsVisibilityKeys.includes(channel.key) : undefined,
      );
      const customerCategoryChannel = st.distributionChannels?.find((channel) =>
        channel.key ? channelsVisibilityKeys.includes(channel.key) : undefined,
      );

      if (
        distributionChannel &&
        customerCategoryChannel &&
        distributionChannel.channelId &&
        customerCategoryChannel.channelId &&
        bu.key &&
        st.storeId
      ) {
        await updateSessionData(
          bu.key,
          st.key,
          st.storeId,
          distributionChannel.channelId,
          customerCategoryChannel.channelId,
          customerCategoryChannel.key ?? '',
        );

        if (reload) {
          router.replace('/');
          router.refresh();
          toast.success(translate('common.businessUnit.connected', { values: { name: bu.name ?? '' } }), {
            position: 'top-center',
            duration: 4500,
            style: {
              height: 'auto',
            },
          });
        }
      }
    },
    [
      selectedBusinessUnit,
      selectedStore,
      sessionBusinessUnitKey,
      businessUnits,
      setSelectedBusinessUnit,
      setSelectedStore,
      updateSessionData,
      router,
      channelsVisibilityKeys,
    ],
  );

  const { totalItems: totalCartItems, addItem } = useCart(selectedBusinessUnit?.key, selectedStore?.key);

  const onLogoutClick = useCallback(() => {
    logout().then(() => {
      clearCache();
      localStorage.clear();
      router.refresh();
      router.push('/login');
    });
  }, [logout, clearCache, router]);

  const [headerSearch, setHeaderSearch] = useState(searchQuery || '');
  const debouncedHeaderSearch = useDebounce(headerSearch, 150);

  const shouldFetchHeaderSearch = !!(selectedStore?.key && debouncedHeaderSearch !== '');
  const { products: headerProducts } = useProductSearch(
    shouldFetchHeaderSearch ? debouncedHeaderSearch : '',
    undefined,
    undefined,
    selectedStore?.key,
    channel?.key,
    priceChannel?.channelId,
    shouldFetchHeaderSearch ? true : undefined,
    false,
  );

  const searchSuggestions = useMemo(() => {
    if (debouncedHeaderSearch === '') {
      return [];
    }
    return headerProducts.map(mapProductSuggestion);
  }, [headerProducts, debouncedHeaderSearch]);

  const onHeaderSearch = useCallback((value: string) => setHeaderSearch(value), []);
  const headerSearchAction = useCallback(
    () => router.push(`/search/?query=${debouncedHeaderSearch}`),
    [router, debouncedHeaderSearch],
  );

  const mappedStores =
    stores?.map((store) => ({ name: store.name ?? store.key ?? '-', value: store.key ?? '-' })) ?? [];

  const mappedBusinessUnits =
    businessUnits?.map((businessUnit) => ({
      name: businessUnit.name ?? businessUnit.key ?? '-',
      value: businessUnit.key ?? '-',
    })) ?? [];

  return {
    account,
    defaultBusinessUnit,
    selectedStore: selectedStore?.key ?? sessionBusinessUnitKey,
    stores: mappedStores,
    selectedBusinessUnit: selectedBusinessUnit?.key ?? sessionBusinessUnitKey,
    businessUnits: mappedBusinessUnits,
    totalCartItems,
    quotes: [],
    headerSearch,
    searchSuggestions,
    addToCart: addItem,
    onBusinessUnitSelect,
    onHeaderSearch,
    headerSearchAction,
    onLogoutClick,
  };
};

export default useHeaderData;
